import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  CreditCard,
  Package,
  Truck,
  MapPin,
  CheckCircle,
  Package2
} from "lucide-react";

const STAGES = [
  { key: "order_placed",      label: "Order Placed",      Icon: ClipboardList },
  { key: "payment_confirmed", label: "Payment Confirmed", Icon: CreditCard },
  { key: "packaged",          label: "Packaged",          Icon: Package },
  { key: "shipped",           label: "Shipped",           Icon: Truck },
  { key: "out_for_delivery",  label: "Out for Delivery",  Icon: MapPin },
  { key: "delivered",         label: "Delivered",         Icon: CheckCircle }
];

const STAGE_INDEX = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));

export default function OrderDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await fetch(`https://eleven11-i6v4.onrender.com/api/orders/${id}`);
      if (!res.ok) return;
      const data = await res.json();
      setOrder(data);
    } catch (err) {
      console.error("Failed to load order:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={styles.page}><div style={styles.loadingWrap}>Loading order...</div></div>
  );

  if (!order) return (
    <div style={styles.page}> <div style={styles.loadingWrap}>Order not found.</div></div>
  );

  const currentIndex = STAGE_INDEX[order.fulfillmentStatus] ?? 0;
  const subtotal = Number(order.subtotal || 0);
  const tax = Number(order.tax || 0);
  const total = Number(order.total || 0);

  const date = new Date(order.createdAt).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric"
  });

  const historyMap = {};
  (order.statusHistory || []).forEach((h) => { historyMap[h.stage] = h; });

  return (
    <div style={styles.page}>
    
      <div style={styles.heroGlow} />

      <main style={styles.container}>

        <section style={styles.header}>
          <div>
            <p style={styles.kicker}>ELEVEN ACCOUNT › ORDERS</p>
            <h1 style={styles.title}>{order.orderNumber}</h1>
            <p style={styles.subtitle}>Placed on {date}</p>
          </div>
          <button style={styles.backButton} onClick={() => navigate("/account/orders")}>
            ← Orders
          </button>
        </section>

        {/* Timeline */}
        <section style={styles.card}>
          <p style={styles.sectionLabel}>FULFILLMENT TRACKING</p>

          <div style={styles.timeline}>
            {STAGES.map((stage, index) => {
              const isCompleted = index <= currentIndex;
              const isCurrent = index === currentIndex;
              const histEntry = historyMap[stage.key];

              const timestamp = histEntry?.timestamp
                ? new Date(histEntry.timestamp).toLocaleString("en-US", {
                    month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
                  })
                : null;

              return (
                <div key={stage.key} style={styles.timelineRow}>
                  <div style={styles.timelineLeft}>
                    <div style={{
                      ...styles.dot,
                      background: isCompleted ? "#0a84ff" : "#E2E5ED",
                      boxShadow: isCurrent ? "0 0 0 4px rgba(10,132,255,0.18)" : "none",
                      transform: isCurrent ? "scale(1.25)" : "scale(1)"
                    }} />
                    {index < STAGES.length - 1 && (
                      <div style={{
                        ...styles.connector,
                        background: index < currentIndex ? "#0a84ff" : "#E2E5ED"
                      }} />
                    )}
                  </div>

                  <div style={styles.timelineContent}>
                    <div style={styles.timelineTop}>
                      <span style={styles.stageIcon}>{stage.icon}</span>
                      <span style={{
                        ...styles.stageLabel,
                        color: isCompleted ? "#050505" : "#b0b4bd",
                        fontWeight: isCurrent ? 800 : isCompleted ? 600 : 500
                      }}>
                        {stage.label}
                      </span>
                      {isCurrent && <span style={styles.currentPill}>Current</span>}
                    </div>
                    {timestamp && <p style={styles.timestamp}>{timestamp}</p>}
                    {histEntry?.note && <p style={styles.note}>{histEntry.note}</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {order.trackingNumber && (
            <div style={styles.trackingBox}>
              <p style={styles.trackingLabel}>
                {order.carrier ? `${order.carrier} Tracking` : "Tracking Number"}
              </p>
              <p style={styles.trackingNumber}>{order.trackingNumber}</p>
            </div>
          )}
        </section>

        {/* Items */}
        <section style={styles.card}>
          <p style={styles.sectionLabel}>ITEMS ORDERED</p>

          {(order.items || []).map((item, i) => (
            <div key={i} style={styles.itemRow}>
              {item.image ? (
                <img
                  src={`${item.image}`}
                  alt={item.title}
                  style={styles.itemImage}
                />
              ) : (
                <div style={styles.itemImagePlaceholder}>📦</div>
              )}
              <div style={styles.itemInfo}>
                <p style={styles.itemTitle}>{item.title || "OEM Part"}</p>
                {item.selectedCondition && (
                  <p style={styles.itemMeta}>Condition: {item.selectedCondition}</p>
                )}
                {item.partNumber && (
                  <p style={styles.itemMeta}>Part #: {item.partNumber}</p>
                )}
              </div>
              <div style={styles.itemRight}>
                <p style={styles.itemQty}>x{item.qty}</p>
                <p style={styles.itemPrice}>
                  ${(Number(item.price) * Number(item.qty)).toFixed(2)}
                </p>
              </div>
            </div>
          ))}

          <div style={styles.totalsSection}>
            <div style={styles.totalRow}><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div style={styles.totalRow}>
              <span>Shipping</span>
              <span style={{ color: "#0a84ff", fontWeight: 700 }}>Free</span>
            </div>
            <div style={styles.totalRow}><span>Tax</span><span>${tax.toFixed(2)}</span></div>
            <div style={styles.totalDivider} />
            <div style={{ ...styles.totalRow, ...styles.totalFinal }}>
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
          </div>
        </section>

        {/* Shipping Address */}
        {order.shippingAddress && (
          <section style={styles.card}>
            <p style={styles.sectionLabel}>SHIPPING ADDRESS</p>
            <p style={styles.addressLine}>{order.customer?.firstName} {order.customer?.lastName}</p>
            <p style={styles.addressLine}>{order.shippingAddress.address}</p>
            {order.shippingAddress.apartment && (
              <p style={styles.addressLine}>{order.shippingAddress.apartment}</p>
            )}
            <p style={styles.addressLine}>
              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}
            </p>
            <p style={styles.addressLine}>{order.shippingAddress.country}</p>
          </section>
        )}

        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button style={styles.invoiceButton} onClick={() => navigate(`/invoice/${order._id}`)}>
            View Invoice
          </button>
        </div>

      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "radial-gradient(circle at top, rgba(255,255,255,0.55), transparent 28%), linear-gradient(180deg, #f5f7fb 0%, #eef1f7 45%, #f7f8fb 100%)",
    fontFamily: "-apple-system, BlinkMacSystemFont, Inter, Helvetica, Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    paddingTop: 90,
    paddingBottom: 80
  },
  heroGlow: {
    position: "absolute",
    top: -180,
    left: "50%",
    transform: "translateX(-50%)",
    width: 720,
    height: 420,
    background: "radial-gradient(circle, rgba(255,255,255,0.9), rgba(210,220,240,0.35), transparent 70%)",
    filter: "blur(10px)",
    pointerEvents: "none"
  },
  container: {
    width: "min(780px, calc(100% - 40px))",
    margin: "0 auto",
    position: "relative",
    zIndex: 2
  },
  loadingWrap: {
    textAlign: "center",
    marginTop: 120,
    fontSize: 18,
    color: "#8a8e98"
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 32
  },
  kicker: {
    margin: 0,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 2.4,
    color: "#7b7f89"
  },
  title: {
    margin: "6px 0 4px",
    fontSize: "clamp(20px, 5vw, 42px)",
    letterSpacing: -1.2,
    fontWeight: 800,
    color: "#050505",
    wordBreak: "keep-all",
    overflowWrap: "normal"
  },

  subtitle: { margin: 0, fontSize: 15, color: "#8a8e98" },
  backButton: {
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(255,255,255,0.75)",
    backdropFilter: "blur(18px)",
    borderRadius: 999,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(20,30,50,0.08)",
    whiteSpace: "nowrap"
  },
  card: {
    borderRadius: 28,
    background: "rgba(255,255,255,0.82)",
    backdropFilter: "blur(24px)",
    border: "1px solid rgba(255,255,255,0.85)",
    boxShadow: "0 20px 50px rgba(24,35,60,0.09)",
    padding: "28px 32px",
    marginBottom: 20
  },
  sectionLabel: {
    margin: "0 0 22px",
    fontSize: 12,
    fontWeight: 900,
    color: "#70747e",
    textTransform: "uppercase",
    letterSpacing: 2.2
  },
  timeline: { display: "flex", flexDirection: "column" },
  timelineRow: { display: "flex", gap: 20, minHeight: 64 },
  timelineLeft: { display: "flex", flexDirection: "column", alignItems: "center", width: 20 },
  dot: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    flexShrink: 0,
    transition: "all 0.2s ease",
    marginTop: 2
  },
  connector: { width: 2, flex: 1, marginTop: 4, borderRadius: 2, minHeight: 32 },
  timelineContent: { paddingBottom: 24, flex: 1 },
  timelineTop: { display: "flex", alignItems: "center", gap: 10 },
  stageIcon: { fontSize: 18, lineHeight: 1 },
  stageLabel: { fontSize: 17, transition: "color 0.2s ease" },
  currentPill: {
    background: "#EEF2FF",
    color: "#4361C2",
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 700
  },
  timestamp: { margin: "4px 0 0", fontSize: 13, color: "#8a8e98", paddingLeft: 28 },
  note: { margin: "2px 0 0", fontSize: 13, color: "#aaa", paddingLeft: 28 },
  trackingBox: {
    marginTop: 24,
    padding: "16px 20px",
    borderRadius: 16,
    background: "#f0f4ff",
    border: "1px solid #dde5ff"
  },
  trackingLabel: {
    margin: 0,
    fontSize: 12,
    fontWeight: 800,
    color: "#4361C2",
    textTransform: "uppercase",
    letterSpacing: 1.4
  },
  trackingNumber: { margin: "6px 0 0", fontSize: 18, fontWeight: 700, color: "#050505", letterSpacing: 0.5 },
  itemRow: { display: "flex", alignItems: "center", gap: 16, padding: "16px 0", borderTop: "1px solid rgba(0,0,0,0.06)" },
  itemImage: { width: 60, height: 60, borderRadius: 12, objectFit: "cover", boxShadow: "0 4px 10px rgba(0,0,0,0.07)" },
  itemImagePlaceholder: { width: 60, height: 60, borderRadius: 12, background: "#f0f2f8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 },
  itemInfo: { flex: 1 },
  itemTitle: { margin: 0, fontSize: 15, fontWeight: 600, color: "#1D1D1F" },
  itemMeta: { margin: "4px 0 0", fontSize: 13, color: "#8a8e98" },
  itemRight: { textAlign: "right" },
  itemQty: { margin: 0, fontSize: 13, color: "#8a8e98" },
  itemPrice: { margin: "4px 0 0", fontSize: 17, fontWeight: 700, color: "#050505" },
  totalsSection: { marginTop: 20, paddingTop: 20, borderTop: "1px solid rgba(0,0,0,0.06)" },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: 15, color: "#4A4F63", marginBottom: 10 },
  totalDivider: { height: 1, background: "rgba(0,0,0,0.06)", margin: "14px 0" },
  totalFinal: { fontSize: 22, fontWeight: 800, color: "#050505", marginBottom: 0 },
  addressLine: { margin: "0 0 4px", fontSize: 16, color: "#4A4F63" },
  invoiceButton: {
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(255,255,255,0.75)",
    backdropFilter: "blur(18px)",
    borderRadius: 999,
    padding: "14px 28px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(20,30,50,0.08)"
  }
};
